import { MinusIcon, Trash2Icon } from 'lucide-react';
import type { ReactElement } from 'react';

import { Button } from '@/components/ui/button';

import { useChatBranding, useChatConfig, useChatStore } from '../ChatProvider';
import { useChatActions } from '../hooks/useChatActions';
import { ColorModeMenu } from './ColorModeMenu';

interface ChatHeaderProps {
  showMinimize?: boolean;
  hideLogo?: boolean;
}

export function ChatHeader({
  showMinimize = true,
  hideLogo = false,
}: ChatHeaderProps): ReactElement {
  const branding = useChatBranding();
  const config = useChatConfig();
  const setOpen = useChatStore((s) => s.setOpen);
  const { clear } = useChatActions();
  const renderMinimize = showMinimize && config.enableMinimizeButton;
  const wordmark = branding.secondaryLogo ?? branding.logo;

  return (
    <header
      className="flex shrink-0 items-center gap-4 px-5 py-3.5 pt-[max(env(safe-area-inset-top),0.875rem)]"
      data-slot="chat-header"
    >
      <h2 className="sr-only">{branding.title}</h2>
      {!hideLogo && (
        <img
          src={wordmark}
          alt=""
          data-slot="chat-header-logo"
          className="h-12 w-auto shrink-0 rounded-full object-cover"
        />
      )}

      <div className="min-w-0 flex-1" />

      <div className="flex shrink-0 items-center gap-1">
        {config.enableClearButton && (
          <Button
            type="button"
            size="icon-lg"
            variant="ghost"
            aria-label="Limpar conversa"
            title="Limpar conversa"
            onClick={clear}
            className="text-muted-foreground hover:text-foreground rounded-full"
          >
            <Trash2Icon aria-hidden />
          </Button>
        )}

        {config.enableDarkMode && <ColorModeMenu />}

        {renderMinimize && (
          <Button
            type="button"
            size="icon-lg"
            variant="ghost"
            aria-label="Minimizar conversa"
            title="Minimizar"
            onClick={() => {
              setOpen(false);
            }}
            className="text-muted-foreground hover:text-foreground rounded-full"
          >
            <MinusIcon aria-hidden />
          </Button>
        )}
      </div>
    </header>
  );
}
